import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Handshake, ReceiptText } from "lucide-react";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { MoneyAmount } from "./MoneyAmount";
import { ExpenseAmount } from "./ExpenseAmount";
import { useInfiniteExpenses, useInfiniteSettlements } from "../api/hooks";
import type { Expense, Group, Settlement } from "../api/types";

/**
 * One group's expenses and payments in a single reverse-chronological list
 * (issue #453).
 *
 * They used to live in two tabs, which meant "what has happened in this group?"
 * couldn't be answered without switching back and forth and mentally
 * interleaving two date-sorted lists. Rows are deliberately dense — one line for
 * what it was, one for who paid — so more of the group fits on a phone screen
 * without scrolling, while the amount stays the thing your eye lands on.
 *
 * "Who owes who" is NOT here: it's a projection of this history, not part of it,
 * and it now lives on its own Settle up screen.
 */
export function GroupActivityTimeline({ group }: { group: Group }): ReactNode {
  const expensesQ = useInfiniteExpenses(group.id);
  const settlementsQ = useInfiniteSettlements(group.id);

  const days = useMemo(() => {
    const expenses = expensesQ.data?.pages.flatMap((p) => p.items) ?? [];
    const settlements = settlementsQ.data?.pages.flatMap((p) => p.items) ?? [];
    return groupActivityByDay(mergeActivity(expenses, settlements));
  }, [expensesQ.data, settlementsQ.data]);

  const isLoading = expensesQ.isLoading || settlementsQ.isLoading;
  // Each list pages independently, so "more" means either still has pages. An
  // older payment can therefore appear above a not-yet-loaded expense; loading
  // more fills the gap in rather than reordering what's already shown.
  const hasMore = expensesQ.hasNextPage || settlementsQ.hasNextPage;
  const isFetchingMore =
    expensesQ.isFetchingNextPage || settlementsQ.isFetchingNextPage;

  if (isLoading) {
    return (
      <section aria-labelledby="activity-heading" className="space-y-6">
        <h2 id="activity-heading" className="text-headline-md">
          Activity
        </h2>
        <Skeleton className="h-40 rounded-md" />
      </section>
    );
  }

  if (days.length === 0) {
    return (
      <section aria-labelledby="activity-heading" className="space-y-6">
        <h2 id="activity-heading" className="text-headline-md">
          Activity
        </h2>
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <span className="grid size-14 place-items-center rounded-full bg-surface-variant text-outline">
            <ReceiptText className="size-6" />
          </span>
          <p className="text-body-md text-on-surface-variant">
            Nothing here yet. Add an expense to get started.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="activity-heading" className="space-y-6">
      <h2 id="activity-heading" className="text-headline-md">
        Activity
      </h2>
      <ol className="space-y-stack-sm">
        {days.map((day) => (
          <li key={day.key}>
            <section className="rounded-md border border-outline-variant/60 bg-surface-container-lowest px-4 py-2">
              <h3 className="border-b border-outline-variant/40 py-2 text-label-md font-bold text-primary">
                {day.label}
              </h3>
              <ol className="divide-y divide-outline-variant/40">
                {day.entries.map((entry) => (
                  <li key={`${entry.kind}-${entry.id}`}>
                    {entry.kind === "expense" ? (
                      <ExpenseRow group={group} expense={entry.expense} />
                    ) : (
                      <SettlementRow
                        group={group}
                        settlement={entry.settlement}
                      />
                    )}
                  </li>
                ))}
              </ol>
            </section>
          </li>
        ))}
      </ol>

      {hasMore ? (
        <div className="text-center">
          <Button
            variant="ghost"
            disabled={isFetchingMore}
            onClick={() => {
              if (expensesQ.hasNextPage) void expensesQ.fetchNextPage();
              if (settlementsQ.hasNextPage) void settlementsQ.fetchNextPage();
            }}
          >
            {isFetchingMore ? "Loading…" : "Load older activity"}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export type ActivityEntry =
  | { kind: "expense"; id: string; at: number; expense: Expense }
  | { kind: "settlement"; id: string; at: number; settlement: Settlement };

/** Interleaves the two histories newest-first. Exported for tests. */
export function mergeActivity(
  expenses: Expense[],
  settlements: Settlement[],
): ActivityEntry[] {
  const entries: ActivityEntry[] = [
    ...expenses.map((expense): ActivityEntry => ({
      kind: "expense",
      id: expense.id,
      at: expenseActivityTimestamp(expense),
      expense,
    })),
    ...settlements.map((settlement): ActivityEntry => ({
      kind: "settlement",
      id: settlement.id,
      at: new Date(settlement.createdAt).getTime(),
      settlement,
    })),
  ];
  // Ties broken by id so the order is stable across renders rather than
  // depending on the input order of two independently-paged lists.
  return entries.sort((a, b) => b.at - a.at || a.id.localeCompare(b.id));
}

/** Selected calendar date plus creation time as a stable within-day order. */
export function expenseActivityTimestamp(expense: Expense): number {
  return new Date(
    `${expense.expenseDate}T${expense.createdAt.slice(11, 23)}`,
  ).getTime();
}

export type ActivityDay = {
  key: string;
  label: string;
  entries: ActivityEntry[];
};

/** Groups an already-sorted timeline into local calendar days. */
export function groupActivityByDay(entries: ActivityEntry[]): ActivityDay[] {
  const days = new Map<string, ActivityDay>();

  for (const entry of entries) {
    const date = new Date(entry.at);
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    const existing = days.get(key);
    if (existing) {
      existing.entries.push(entry);
      continue;
    }
    days.set(key, {
      key,
      label: date.toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      entries: [entry],
    });
  }

  return [...days.values()];
}

const ROW_CLASS =
  "flex items-center gap-2.5 rounded-sm px-1 py-3 transition-colors";

function ExpenseRow({
  group,
  expense,
}: {
  group: Group;
  expense: Expense;
}): ReactNode {
  const total = expense.items.reduce((sum, item) => sum + item.cost, 0);

  return (
    <Link
      to={`/groups/${group.id}/expenses/${expense.id}`}
      className={`${ROW_CLASS} hover:bg-surface-container/50`}
      // The rail is aria-hidden, so the accessible name carries the date too.
      aria-label={`${expense.label}, ${new Date(
        `${expense.expenseDate}T12:00:00`,
      ).toLocaleDateString()}`}
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-sm bg-primary-fixed text-primary">
        <ReceiptText className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block break-words text-body-md">{expense.label}</span>
        {/* `text-label-md`, not `-sm`: the small label style is uppercase in
            this design system, which would shout "LIAM PAID". */}
        <span className="block truncate text-label-md text-on-surface-variant">
          {payerSummary(group, expense)}
        </span>
      </span>
      <ExpenseAmount
        minor={total}
        currency={expense.currency}
        groupCharge={expense.groupCharge}
        groupCurrency={group.currency}
        className="shrink-0 text-body-md font-bold"
      />
    </Link>
  );
}

function SettlementRow({
  group,
  settlement,
}: {
  group: Group;
  settlement: Settlement;
}): ReactNode {
  const payer = nameOf(group, settlement.payerId);
  const payee = nameOf(group, settlement.payeeId);

  return (
    <div className={ROW_CLASS}>
      <span className="grid size-8 shrink-0 place-items-center rounded-sm bg-tertiary-container text-on-tertiary-container">
        <Handshake className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body-md">
          {payer} paid {payee}
        </span>
        <span className="block truncate text-label-md text-on-surface-variant">
          {settlement.note ? settlement.note : "Payment"}
        </span>
      </span>
      <MoneyAmount
        minor={settlement.amount}
        currency={settlement.currency}
        className="shrink-0 text-body-md font-bold text-tertiary"
      />
    </div>
  );
}

function nameOf(group: Group, participantId: string): string {
  return (
    group.participants.find((p) => p.id === participantId)?.displayName ??
    "Someone"
  );
}

/**
 * "Who paid" for a row — the thing #453 asks to be visible without opening the
 * expense. Multiple payers collapse to the first name plus a count so the line
 * never wraps.
 */
export function payerSummary(group: Group, expense: Expense): string {
  const payers = expense.payments.filter((p) => p.amount > 0);
  if (payers.length === 0) return "No payer recorded";

  const first = nameOf(group, payers[0]!.participantId);
  if (payers.length === 1) return `${first} paid`;
  const others = payers.length - 1;
  return `${first} + ${others} other${others === 1 ? "" : "s"} paid`;
}
